import { IGlimpseReport } from '../../customer-shared/models/IGlimpseReport';
import dayjs from 'dayjs';
import { Qsr } from '../../customer-shared/models/qsr.types';

const subwayCustomerIds = [98, 95, 100, 139, 1014, 1050, 1060, 1225];
const fiveCustomerIds = [1147, 1170, 1171];
const countingCustomerIds = [1067, 1074];
const countingLocationIds = [1125];

export function isSubwayCustomer(customerId: number, locationId?: number): Qsr {
  if (subwayCustomerIds.includes(customerId)) return 'subway';
  if (fiveCustomerIds.includes(customerId)) return 'five';
  if (countingCustomerIds.includes(customerId)) return 'counting';
  if (countingLocationIds.includes(locationId)) return 'counting';
  return false;
}

const categoryCosts: Record<string, number> = {
  '6-inch': 0.9,
  Flatbread: 1.95, // 6"=1.03, 12"=1.95 => ~1.5 on average
  '6-inch Flatbread': 1.03, // 6"=1.03, 12"=1.95 => ~1.5 on average
  Footlong: 1.7,
  Salad: 1.32,
  Wrap: 2.05,

  Mini: 1.62,
  // Random
  Slider: 0.7,
  Soup: 0.5,

  //
  'Apple Sauce': 0.33,
  'Hot Drink': 0.2,
  'Soft Drink 20oz': 0.2,
  'Soft Drink 30oz': 0.3,
  'Bottled Drink': 1,

  Chips: 0.46,
  Cookie: 0.2,

  '6-inch Meal': 0.9 + 0.85,
  'Cookie Platter': 8.12,
  'Footlong Meal': 1.7 + 0.85,
  'Kids Meal': 0.9 + 0.57,
  'Lunch Box': 0.9 + 0.57,
  'Sub Platter': 12.52,
  'Wrap Platter': 11.4,
  //BWW
  '6 Wings': 10.49,
  '10 Wings': 14.99,
  '15 Wings': 21.49,
  '20 Wings': 26.99,
  '30 Wings': 38.49,
  '40 Wings': 49.49,
  '3 Tenders': 11.49,
  '5 Tenders': 12.99,
  'Sandwich/Wrap': 11.49,
  '6 Wings Bundle': 9.99,
  '10 Wings Bundle': 15.99,
  '3 Tenders Bundle': 12.49,
  'Sandwich/Wrap Bundle': 12.49,
  'Party Salad': 18.49,
  'Carbonated Drink': 1.99,
};

export function calculateEmployeeRoi(report: IGlimpseReport) {
  let clockInOuts = report.employeeClockings.map(x => ({
    employee: x.employeeName,
    minutes: 0,
    end: x.clockOut,
    start: x.clockIn,
    rate: x.hourlyWage,
    posAlias: x.employeeTag,
  }));
  const saleTimes = report.allSales.map(s => s.date).sort();

  const minClockIn = clockInOuts.reduce((min, item) => Math.min(min, dayjs(item.start).startOf('hour').valueOf()), Infinity);
  const maxClockOut = clockInOuts.reduce((max, item) => Math.max(max, dayjs(item.end).startOf('hour').valueOf()), 0);

  const startHour = dayjs(Math.min(minClockIn, dayjs(saleTimes[0]).startOf('hour').valueOf()));
  const endHour = dayjs(
    Math.max(
      maxClockOut,
      saleTimes.length > 0
        ? dayjs(saleTimes[saleTimes.length - 1])
            .startOf('hour')
            .valueOf()
        : 0,
    ),
  );

  // console.log(report.allSales.reduce((sum, sale) => sum + sale.val, 0));
  //
  function roundTimeToHour(time: dayjs.Dayjs) {
    if (time.minute() < 30) return time.startOf('h');
    else return time.startOf('h').add(1, 'h');
  }

  const employeeHours: {
    employee: string;
    startHour: dayjs.Dayjs;
    endHour: dayjs.Dayjs;
    rate: number;
    cost: number;
  }[] = clockInOuts.map(item => ({
    employee: item.employee,
    startHour: dayjs(item.start),
    endHour: dayjs(item.end),
    rate: item.rate,
    cost: ((dayjs(item.end).valueOf() - dayjs(item.start).valueOf()) / 3600000) * item.rate,
  }));

  const roiTable = [];

  for (let time = startHour; !time.isAfter(endHour); time = time.add(1, 'hour')) {
    const employeeCosts = employeeHours
      .filter(clockInOut => !clockInOut.startHour.startOf('hour').isAfter(time) && !clockInOut.endHour.startOf('hour').isBefore(time))
      .reduce((sum, emp) => {
        let timeWorked = 60;
        if (!emp.startHour.isBefore(time)) {
          timeWorked -= emp.startHour.diff(time, 'minute');
        }
        if (!emp.endHour.isAfter(time.add(1, 'hour'))) {
          timeWorked -= time.add(1, 'hour').diff(emp.endHour, 'minute');
        }

        return sum + (timeWorked / 60) * emp.rate;
      }, 0);
    const sales = report.allSales.filter(sale => dayjs(sale.date).startOf('hour').isSame(time));
    const netRevenue = sales.reduce((sum, sale) => sum + sale.val, 0);
    const royalty = netRevenue * (0.08 + 0.045);
    const revenue = netRevenue - royalty;
    const productionCosts = sales.reduce((sum, sale) => sum + (categoryCosts[sale.cat] || 0), 0);
    roiTable.push({
      timeFormatted: time.format('hh:mma'),
      time: time,
      employeeCosts,
      netRevenue,
      revenue,
      royalty,
      productionCosts,
      total: revenue - productionCosts - employeeCosts,
    });
  }

  const totalIn = roiTable.reduce((sum, hour) => sum + hour.netRevenue, 0);
  const totalRoyalty = roiTable.reduce((sum, hour) => sum + hour.royalty, 0);
  const totalProductionCosts = roiTable.reduce((sum, hour) => sum + hour.productionCosts, 0);
  const totalEmployeeCosts = roiTable.reduce((sum, hour) => sum + hour.employeeCosts, 0);
  const totalOut = totalProductionCosts + totalEmployeeCosts + totalRoyalty;

  return {
    roi: totalIn / totalOut - 1,
    totalIn,
    totalOut,
    totalRoyalty,
    totalProductionCosts,
    totalEmployeeCosts,
    totalProfit: totalIn - totalOut,
    employeeHours,
    startHour: startHour.format('hh:mma'),
    endHour: endHour.format('hh:mma'),
    roiTable,
  };
}

export function calculateCheckProductionTimes(state: IGlimpseReport): {
  checkNo: string;
  pt: number[];
  date: dayjs.Dayjs;
}[] {
  if (!state?.allTransactions) return [];

  let salesWithProductionTimes = state.allTransactions.reduce(
    (acc, curr) => {
      if (!acc.some(x => x.saleId === curr.sId)) acc.push({ saleId: curr.sId, productionTime: curr.pt });

      return acc;
    },
    [] as { saleId: string; productionTime: number }[],
  );

  return state.allSales
    .filter(x => salesWithProductionTimes.some(s => s.saleId === x.id))
    .reduce(
      (acc, sale) => {
        let ticketStats = acc.find(x => x.checkNo === sale.checkNo);
        let t = salesWithProductionTimes.find(s => s.saleId === sale.id);
        if (ticketStats) {
          ticketStats.pt = [...ticketStats.pt, t.productionTime];
        } else {
          acc.push({
            checkNo: sale.checkNo,
            pt: [t.productionTime],
            date: dayjs(sale.date),
          });
        }
        return acc;
      },
      [] as { checkNo: string; pt: number[]; date: dayjs.Dayjs }[],
    )
    .filter(x => x.pt.some(pt => pt > 0));
}
